/**
 * Staff "view as user" impersonation state (client side).
 *
 * When a staff member chooses "Open dashboard" for a user on /super-admin, we
 * persist that target in localStorage. The API fetcher then attaches an
 * `X-Impersonate-User-Id` header to every request EXCEPT admin-console calls,
 * and the gateway — after verifying the caller is staff — scopes the request
 * to the target user. So the whole dashboard renders as that user.
 *
 * The gateway is the security boundary: it only honors the header for verified
 * staff identities. This module is just convenience state; setting it as a
 * non-staff user does nothing server-side.
 */

const ID_KEY = 'dca_impersonate_user_id'
const LABEL_KEY = 'dca_impersonate_label'

export function getImpersonatedUserId(): string | null {
  try {
    return localStorage.getItem(ID_KEY)
  } catch {
    return null
  }
}

export function getImpersonatedLabel(): string | null {
  try {
    return localStorage.getItem(LABEL_KEY)
  } catch {
    return null
  }
}

export function isImpersonating(): boolean {
  return Boolean(getImpersonatedUserId())
}

export function setImpersonatedUser(userId: string, label: string): void {
  try {
    localStorage.setItem(ID_KEY, userId)
    localStorage.setItem(LABEL_KEY, label)
  } catch {
    /* ignore storage failures */
  }
}

export function clearImpersonation(): void {
  try {
    localStorage.removeItem(ID_KEY)
    localStorage.removeItem(LABEL_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * The impersonation header must NOT be sent on admin-console requests, or a
 * staff member browsing as another user would lose access to /api/admin/*
 * (those routes gate on the real staff identity). Everything else carries it.
 */
export function shouldImpersonatePath(path: string): boolean {
  // /api/billing/admin/* are staff-only billing actions (promo codes, org
  // seat checkout) and need the real staff identity too.
  return !path.startsWith('/api/admin/') && !path.startsWith('/api/billing/admin/')
}
